import { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';

interface ToolsSettings {
  feeCollectorAddress: string;
  multisendFee: number;
  airdropFee: number;
  vestingFee: number;
}

interface ToolsSettingsContextType {
  settings: ToolsSettings;
  isLoading: boolean;
}

const DEFAULT_TOOLS_SETTINGS: ToolsSettings = {
  feeCollectorAddress: "0x31dB0dBa8d2D0d2a58D3286E1D3a1aF778c832A0",
  multisendFee: 0.1, // 0.1 BNB
  airdropFee: 0.15, // 0.15 BNB
  vestingFee: 0.2, // 0.2 BNB
};

const ToolsSettingsContext = createContext<ToolsSettingsContextType | undefined>(undefined);

export function ToolsSettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<ToolsSettings>(DEFAULT_TOOLS_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, "settings", "fees"),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          // Get the BSC network fees (chainId 56)
          const bscFees = data.networkFees?.[56] || {};
          const collectors = data.collectors || [];
          
          setSettings({
            feeCollectorAddress: collectors[0]?.address || DEFAULT_TOOLS_SETTINGS.feeCollectorAddress,
            multisendFee: parseFloat(bscFees.toolsFee) || DEFAULT_TOOLS_SETTINGS.multisendFee,
            airdropFee: parseFloat(bscFees.toolsFee) || DEFAULT_TOOLS_SETTINGS.airdropFee,
            vestingFee: parseFloat(bscFees.toolsFee) || DEFAULT_TOOLS_SETTINGS.vestingFee,
          });
        }
        setIsLoading(false);
      },
      (error) => {
        console.error('Error loading tools settings:', error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return (
    <ToolsSettingsContext.Provider value={{ settings, isLoading }}>
      {children}
    </ToolsSettingsContext.Provider>
  );
}

export function useToolsSettings() {
  const context = useContext(ToolsSettingsContext);
  if (context === undefined) {
    throw new Error('useToolsSettings must be used within a ToolsSettingsProvider');
  }
  return context;
}
